import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Pencil, Trash2, Download, ChevronDown, ChevronUp, X, Search } from 'lucide-react';
import {
  adminInventorySales,
  adminRevenuePeriods,
  adminProducts,
  type InventorySalesRow,
  type RevenuePeriod,
} from '../../api/admin';

// ── Helpers ──────────────────────────────────────────────────────
const fmt = (n: number) =>
  `₹${(Number.isFinite(n) ? n : 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const intFmt = (n: number) => (Number.isFinite(n) ? n : 0).toLocaleString('en-IN');
const dateShort = (s: string | null) =>
  s
    ? new Date(s).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kolkata',
      })
    : '—';
const periodLabel = (p: RevenuePeriod) =>
  `${dateShort(p.period_from)} → ${dateShort(p.period_to)}`;

function downloadCSV(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
    download: filename,
  });
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Types ─────────────────────────────────────────────────────────
interface ProductOption {
  id: string;
  name: string;
  brand: string;
  flavor?: string | null;
  strength: number;
  strengthUnit: string;
  price?: number | null;
}

interface DraftRow {
  product_name: string;
  quantity: string;
  sold: string;
  inhouse_use: string;
  sold_at: string;
  adjustment_amount: string;
  remarks: string;
}

const emptyDraft = (): DraftRow => ({
  product_name: '',
  quantity: '',
  sold: '',
  inhouse_use: '',
  sold_at: '',
  adjustment_amount: '',
  remarks: '',
});

// total = sold × sold_at − adjustment  (NaN-safe)
const safeNum = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const safeInt = (s: string) => { const n = parseInt(s, 10); return isNaN(n) ? 0 : n; };
const rowTotal = (r: DraftRow) =>
  safeInt(r.sold) * safeNum(r.sold_at) - safeNum(r.adjustment_amount);

// ── Product Picker (portaled to body to escape overflow clipping) ──
interface PickerProps {
  value: string;
  products: ProductOption[];
  isDark: boolean;
  isOpen: boolean;
  search: string;
  brand: string;
  brands: string[];
  onToggle: () => void;
  onSearch: (v: string) => void;
  onBrand: (v: string) => void;
  onSelect: (label: string, price: number | null) => void;
  onClose: () => void;
}

function ProductPicker({
  value, products, isDark, isOpen, search, brand, brands,
  onToggle, onSearch, onBrand, onSelect, onClose,
}: PickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const filtered = useMemo(() => {
    let list = products;
    if (brand !== 'all') list = list.filter((p) => p.brand === brand);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q) ||
          (p.flavor ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, brand, search]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    onToggle();
  };

  const muted = isDark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={`w-full text-left text-xs font-medium leading-tight px-2.5 py-1.5 rounded-lg border transition-colors flex items-center justify-between gap-1 ${
          isDark
            ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500 text-white'
            : 'bg-gray-50 border-gray-200 hover:border-gray-300 text-gray-800'
        }`}
      >
        <span className={`truncate ${!value ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}`}>
          {value || '— select product —'}
        </span>
        <ChevronDown size={12} className={`shrink-0 transition-transform ${muted} ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 260), zIndex: 9999 }}
          className={`rounded-lg border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
        >
          {/* Search */}
          <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="relative">
              <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                type="text"
                placeholder="Search products…"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                autoFocus
                className={`w-full pl-8 pr-3 py-1.5 rounded-md border text-xs outline-none ${
                  isDark
                    ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                    : 'bg-gray-50 border-gray-200 placeholder-gray-400'
                }`}
              />
            </div>
          </div>
          {/* Brand tabs */}
          <div className={`flex gap-1 px-2 py-1.5 overflow-x-auto border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            {['all', ...brands].map((b) => (
              <button
                key={b}
                onClick={() => onBrand(b)}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${
                  brand === b
                    ? 'bg-emerald-500 text-white'
                    : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {b === 'all' ? 'All' : b}
              </button>
            ))}
          </div>
          {/* Product list */}
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 && (
              <p className={`text-xs text-center py-4 ${muted}`}>No products found</p>
            )}
            {filtered.map((p) => {
              const label = p.name;
              const isSelected = value === label;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelect(label, p.price ?? null)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                    isSelected ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') : ''
                  } ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                >
                  <div className="min-w-0">
                    <p className={`text-xs font-medium truncate ${isSelected ? 'text-emerald-500' : ''}`}>{p.name}</p>
                    <p className={`text-[10px] ${muted}`}>
                      {p.brand}{p.flavor ? ` · ${p.flavor}` : ''}{p.strength ? ` · ${p.strength}${p.strengthUnit ?? ''}` : ''}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function InventorySalesTab({ isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<InventorySalesRow[]>([]);
  const [periods, setPeriods] = useState<RevenuePeriod[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productLoadError, setProductLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedPeriodId, setExpandedPeriodId] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<'all' | 'period'>('all');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');

  // Add multi-row modal
  const [addModal, setAddModal] = useState<{ periodId: string; draftRows: DraftRow[] } | null>(null);
  const [addError, setAddError] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const addModalRef = useRef<HTMLDivElement>(null);
  const tableAreaRef = useRef<HTMLDivElement>(null);

  // Picker state for add modal rows
  const [pickerOpenIdx, setPickerOpenIdx] = useState<number | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerBrand, setPickerBrand] = useState('all');

  // Edit single-row modal
  const [editModal, setEditModal] = useState<{ id: string; periodId: string; v: DraftRow } | null>(null);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editPickerOpen, setEditPickerOpen] = useState(false);
  const [editPickerSearch, setEditPickerSearch] = useState('');
  const [editPickerBrand, setEditPickerBrand] = useState('all');

  // Style tokens
  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const inp = `px-2.5 py-1.5 rounded-lg border text-sm outline-none w-full ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;
  const muted = isDark ? 'text-gray-400' : 'text-gray-500';
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const thCls = `text-xs font-medium py-2 px-2 whitespace-nowrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`;

  const brands = useMemo(() => {
    const s = new Set(products.map((p) => p.brand));
    return Array.from(s).sort();
  }, [products]);

  // ── Load ─────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    const [salesRes, periodsRes, prodsRes] = await Promise.allSettled([
      adminInventorySales.list(),
      adminRevenuePeriods.list(),
      adminProducts.list(),
    ]);
    if (salesRes.status === 'fulfilled') setRows(salesRes.value);
    if (periodsRes.status === 'fulfilled') setPeriods(periodsRes.value);
    if (prodsRes.status === 'fulfilled') {
      setProducts(
        (prodsRes.value.items as unknown as ProductOption[]).sort((a, b) =>
          `${a.brand} ${a.name}`.localeCompare(`${b.brand} ${b.name}`)
        )
      );
      setProductLoadError('');
    } else {
      setProductLoadError('Product list failed to load — the product picker will be empty. Refresh the page to retry.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ── Group rows by period ──────────────────────────────────────────
  const rowsByPeriod = useMemo(() => {
    const m: Record<string, InventorySalesRow[]> = {};
    for (const r of rows) {
      if (!m[r.revenue_period_id]) m[r.revenue_period_id] = [];
      m[r.revenue_period_id].push(r);
    }
    return m;
  }, [rows]);

  const visiblePeriods = useMemo(() => {
    const sortDesc = (arr: RevenuePeriod[]) => arr.slice().sort((a, b) => (b.period_from ?? '').localeCompare(a.period_from ?? ''));
    if (datePreset === 'period') return sortDesc(periods.filter(p => p.id === selectedPeriodId));
    return sortDesc(periods);
  }, [periods, datePreset, selectedPeriodId]);

  // ── CSV export ────────────────────────────────────────────────────
  const exportCsv = () => {
    const header = ['Period From', 'Period To', 'Product', 'Qty', 'Sold', 'Inhouse Use', 'Sold at', 'Adjustment', 'Total', 'Remarks'];
    const visiblePeriodIds = new Set(visiblePeriods.map((p) => p.id));
    const csvRows = rows
      .filter((r) => visiblePeriodIds.has(r.revenue_period_id))
      .map((r) => {
        const p = periods.find((x) => x.id === r.revenue_period_id);
        return [p?.period_from ?? '', p?.period_to ?? '', r.product_name, r.quantity, r.sold, r.inhouse_use, r.sold_at, r.adjustment_amount, r.total, r.remarks ?? ''];
      });
    downloadCSV('ZeroSmoke_InventorySales.csv', header, csvRows);
  };

  // ── Add modal helpers ─────────────────────────────────────────────
  const openAdd = (periodId: string) => {
    setAddModal({ periodId, draftRows: [emptyDraft()] });
    setAddError('');
    setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all');
  };

  const setDraftRow = (idx: number, key: keyof DraftRow, val: string) =>
    setAddModal((m) =>
      m ? { ...m, draftRows: m.draftRows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)) } : m
    );

  const addDraftRow = () =>
    setAddModal((m) => (m ? { ...m, draftRows: [...m.draftRows, emptyDraft()] } : m));

  const removeDraftRow = (idx: number) =>
    setAddModal((m) =>
      m && m.draftRows.length > 1
        ? { ...m, draftRows: m.draftRows.filter((_, i) => i !== idx) }
        : m
    );

  const hasData = (r: DraftRow) =>
    safeInt(r.quantity) > 0 || safeInt(r.sold) > 0 || safeInt(r.inhouse_use) > 0 ||
    safeNum(r.sold_at) > 0 || safeNum(r.adjustment_amount) > 0 || r.remarks.trim().length > 0;

  const saveAdd = async () => {
    if (!addModal) return;
    const valid = addModal.draftRows.filter((r) => r.product_name.trim());
    const incomplete = addModal.draftRows.filter((r) => !r.product_name.trim() && hasData(r));
    if (valid.length === 0) {
      setAddError('Select at least one product');
      requestAnimationFrame(() => tableAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
      return;
    }
    if (incomplete.length > 0) {
      setAddError(`${incomplete.length} row${incomplete.length > 1 ? 's have' : ' has'} data but no product selected — pick a product or clear those rows before saving.`);
      requestAnimationFrame(() => tableAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
      return;
    }
    for (const r of valid) {
      const qty = safeInt(r.quantity);
      if (qty > 0) {
        const used = safeInt(r.sold) + safeInt(r.inhouse_use);
        if (used > qty) {
          setAddError(`"${r.product_name}": sold + inhouse use (${used}) exceeds quantity (${qty})`);
          return;
        }
      }
    }
    setAddSaving(true);
    setAddError('');
    try {
      await adminInventorySales.bulkCreate({
        revenue_period_id: addModal.periodId,
        items: valid.map((r) => ({
          product_name: r.product_name.trim(),
          quantity: safeInt(r.quantity),
          sold: safeInt(r.sold),
          inhouse_use: safeInt(r.inhouse_use),
          sold_at: safeNum(r.sold_at),
          adjustment_amount: safeNum(r.adjustment_amount),
          total: rowTotal(r),
          remarks: r.remarks.trim() || null,
        })),
      });
      setAddModal(null);
      load();
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to save');
      requestAnimationFrame(() => tableAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
    } finally {
      setAddSaving(false);
    }
  };

  // ── Edit modal helpers ────────────────────────────────────────────
  const openEdit = (r: InventorySalesRow) => {
    setEditModal({
      id: r.id,
      periodId: r.revenue_period_id,
      v: {
        product_name: r.product_name,
        quantity: String(r.quantity ?? ''),
        sold: String(r.sold ?? ''),
        inhouse_use: String(r.inhouse_use ?? ''),
        sold_at: String(r.sold_at ?? ''),
        adjustment_amount: String(r.adjustment_amount ?? ''),
        remarks: r.remarks ?? '',
      },
    });
    setEditError('');
    setEditPickerOpen(false); setEditPickerSearch(''); setEditPickerBrand('all');
  };

  const setEV = (key: keyof DraftRow, val: string) =>
    setEditModal((m) => (m ? { ...m, v: { ...m.v, [key]: val } } : m));

  const saveEdit = async () => {
    if (!editModal) return;
    if (!editModal.v.product_name.trim()) { setEditError('Select a product'); return; }
    const v = editModal.v;
    const editQty = safeInt(v.quantity);
    if (editQty > 0) {
      const editUsed = safeInt(v.sold) + safeInt(v.inhouse_use);
      if (editUsed > editQty) {
        setEditError(`sold + inhouse use (${editUsed}) exceeds quantity (${editQty})`);
        return;
      }
    }
    setEditSaving(true);
    setEditError('');
    try {
      await adminInventorySales.update(editModal.id, {
        revenue_period_id: editModal.periodId,
        product_name: v.product_name.trim(),
        quantity: safeInt(v.quantity),
        sold: safeInt(v.sold),
        inhouse_use: safeInt(v.inhouse_use),
        sold_at: safeNum(v.sold_at),
        adjustment_amount: safeNum(v.adjustment_amount),
        total: rowTotal(v),
        remarks: v.remarks.trim() || null,
      });
      setEditModal(null);
      load();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const remove = async (id: string) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await adminInventorySales.delete(id);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  // ── Numeric input helper ──────────────────────────────────────────
  const numInput = (
    val: string,
    onChange: (v: string) => void,
    pattern: RegExp,
    placeholder = '0',
    kind: 'int' | 'dec' = 'dec'
  ) => {
    const displayVal = kind === 'int' && val ? parseInt(val, 10).toLocaleString('en-IN') : val;
    return (
      <input
        type="text"
        inputMode={kind === 'int' ? 'numeric' : 'decimal'}
        value={displayVal}
        placeholder={placeholder}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value.replace(pattern, ''))}
        className={`${inp} text-right`}
      />
    );
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <button
          onClick={exportCsv}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${
            isDark
              ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
              : 'border-gray-200 text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Download size={15} /> Export CSV
        </button>
        <div className={`ml-auto flex items-center gap-4 text-sm ${muted}`}>
          <span>Total Sold pieces: <b>{intFmt(visiblePeriods.flatMap(p => rowsByPeriod[p.id] ?? []).reduce((s, r) => s + (r.sold ?? 0), 0))}</b></span>
          <span>Total Used pieces: <b>{intFmt(visiblePeriods.flatMap(p => rowsByPeriod[p.id] ?? []).reduce((s, r) => s + (r.quantity ?? 0), 0))}</b></span>
        </div>
      </div>

      {/* Period date filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Period:</span>
        <button
          onClick={() => { setDatePreset('all'); setSelectedPeriodId(''); }}
          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
            datePreset === 'all'
              ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/20'
              : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'
          }`}
        >
          All time
        </button>
        <select
          value={selectedPeriodId}
          onChange={e => {
            const id = e.target.value;
            setSelectedPeriodId(id);
            setDatePreset(id ? 'period' : 'all');
          }}
          className={`px-2 py-1 rounded-lg border text-xs font-semibold transition-all ${
            datePreset === 'period'
              ? 'bg-emerald-500 text-white border-emerald-500'
              : isDark ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'
          }`}
        >
          <option value="">Select period…</option>
          {[...periods]
            .sort((a, b) => (b.period_from ?? '').localeCompare(a.period_from ?? ''))
            .map(p => (
              <option key={p.id} value={p.id}>
                {dateShort(p.period_from)} → {dateShort(p.period_to)}
              </option>
            ))}
        </select>
      </div>

      {productLoadError && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600">
          ⚠ {productLoadError}
        </div>
      )}

      {loading && <div className={`text-sm text-center py-8 ${muted}`}>Loading…</div>}

      {!loading && periods.length === 0 && (
        <div className={`text-sm text-center py-8 ${muted}`}>
          No revenue periods found. Add a period in the Revenue tab first.
        </div>
      )}
      {!loading && periods.length > 0 && visiblePeriods.length === 0 && (
        <div className={`text-sm text-center py-8 ${muted}`}>No periods match the current filter.</div>
      )}

      {/* Period cards */}
      {!loading && visiblePeriods.map((period) => {
        const periodRows = (rowsByPeriod[period.id] ?? []).slice().sort((a, b) => (b.quantity ?? 0) - (a.quantity ?? 0));
        const isOpen = expandedPeriodId === period.id;
        const totalSold = periodRows.reduce((s, r) => s + (r.sold ?? 0), 0);
        const totalVal = periodRows.reduce((s, r) => s + (r.total ?? 0), 0);

        return (
          <div key={period.id} className={`rounded-xl border ${card}`}>
            {/* Card header */}
            <div
              className={`flex items-center gap-3 px-5 py-4 cursor-pointer select-none ${
                isOpen ? (isDark ? 'border-b border-gray-800' : 'border-b border-gray-100') : ''
              }`}
              onClick={() => setExpandedPeriodId(isOpen ? null : period.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{periodLabel(period)}</div>
                <div className={`text-xs mt-0.5 ${muted}`}>
                  {periodRows.length === 0
                    ? 'No entries'
                    : `${periodRows.length} product${periodRows.length === 1 ? '' : 's'} · ${intFmt(totalSold)} sold · ${fmt(totalVal)}`}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); openAdd(period.id); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg"
              >
                <Plus size={13} /> Add Sales
              </button>
              <span className={muted}>
                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </span>
            </div>

            {/* Expanded table */}
            {isOpen && (
              <div className="overflow-x-auto">
                {periodRows.length === 0 ? (
                  <div className={`text-sm text-center py-6 ${muted}`}>No sales entries for this period yet.</div>
                ) : (
                  <table className="w-full text-sm min-w-[760px]">
                    <thead>
                      <tr className={`text-left border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                        <th className={`${thCls} text-left`}>Product</th>
                        <th className={`${thCls} text-right`}>Qty</th>
                        <th className={`${thCls} text-right`}>Sold</th>
                        <th className={`${thCls} text-right`}>Inhouse</th>
                        <th className={`${thCls} text-right`}>Sold at</th>
                        <th className={`${thCls} text-right`}>Adjustment</th>
                        <th className={`${thCls} text-right`}>
                          <div>Total (₹)</div>
                          <div className={`text-[9px] font-normal mt-0.5`}>sold × sold_at − adj</div>
                        </th>
                        <th className={`${thCls} text-left`}>Remarks</th>
                        <th className="w-16" />
                      </tr>
                    </thead>
                    <tbody>
                      {periodRows.map((r) => (
                        <tr key={r.id} className={`border-b last:border-0 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                          <td className="py-2.5 px-2 font-medium text-sm">{r.product_name}</td>
                          <td className="py-2.5 px-2 text-right text-sm">{intFmt(r.quantity)}</td>
                          <td className="py-2.5 px-2 text-right text-sm">{intFmt(r.sold)}</td>
                          <td className="py-2.5 px-2 text-right text-sm">{intFmt(r.inhouse_use)}</td>
                          <td className="py-2.5 px-2 text-right text-sm">{fmt(r.sold_at)}</td>
                          <td className={`py-2.5 px-2 text-right text-sm ${r.adjustment_amount ? 'text-amber-500' : muted}`}>
                            {r.adjustment_amount ? fmt(r.adjustment_amount) : '—'}
                          </td>
                          <td className="py-2.5 px-2 text-right font-semibold text-emerald-500 text-sm">{fmt(r.total)}</td>
                          <td className={`py-2.5 px-2 text-xs ${muted} max-w-[220px]`}>
                            <div className="break-words whitespace-pre-wrap">{r.remarks ?? '—'}</div>
                          </td>
                          <td className="py-2.5 px-2">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => openEdit(r)} className={`${muted} hover:text-emerald-500`}><Pencil size={14} /></button>
                              <button onClick={() => remove(r.id)} className={`${muted} hover:text-red-500`}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className={isDark ? 'bg-gray-800/40' : 'bg-gray-50'}>
                        <td className={`py-2 px-2 text-xs font-semibold ${muted}`}>Total</td>
                        <td className="py-2 px-2 text-right text-xs font-semibold">{intFmt(periodRows.reduce((s, r) => s + r.quantity, 0))}</td>
                        <td className="py-2 px-2 text-right text-xs font-semibold">{intFmt(totalSold)}</td>
                        <td className="py-2 px-2 text-right text-xs font-semibold">{intFmt(periodRows.reduce((s, r) => s + r.inhouse_use, 0))}</td>
                        <td colSpan={2} />
                        <td className="py-2 px-2 text-right text-xs font-semibold text-emerald-500">{fmt(totalVal)}</td>
                        <td colSpan={2} />
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Add multi-row modal ─────────────────────────────────────── */}
      {addModal && (() => {
        const period = periods.find((p) => p.id === addModal.periodId);
        const validCount = addModal.draftRows.filter((r) => r.product_name.trim()).length;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setAddModal(null)}
          >
            <div
              ref={addModalRef}
              className={`w-full max-w-5xl rounded-2xl border p-6 flex flex-col ${card}`} style={{ height: '80vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">Add Inventory Sales</h3>
                  {period && <p className={`text-sm ${muted}`}>{periodLabel(period)}</p>}
                  <p className={`text-xs mt-1 ${muted}`}>Total = Sold × Sold at − Adjustment</p>
                </div>
                <button onClick={() => setAddModal(null)} className={muted}><X size={20} /></button>
              </div>

              {addError && <div className="mb-3 text-sm text-red-500">{addError}</div>}

              <div ref={tableAreaRef} className="flex-1 overflow-auto min-h-0">
                <table className="w-full min-w-[820px] text-sm">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`${thCls} text-left w-52`}>Product <span className="text-red-500">*</span></th>
                      <th className={`${thCls} text-right w-20`}>Qty</th>
                      <th className={`${thCls} text-right w-20`}>Sold</th>
                      <th className={`${thCls} text-right w-24`}>Inhouse</th>
                      <th className={`${thCls} text-right w-28`}>Sold at (₹)</th>
                      <th className={`${thCls} text-right w-28`}>Adjustment (₹)</th>
                      <th className={`${thCls} text-right w-32`}>
                        <div>Total (auto)</div>
                        <div className={`text-[9px] font-normal ${muted} mt-0.5`}>sold × sold_at − adj</div>
                      </th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {addModal.draftRows.map((dr, idx) => {
                      const tot = rowTotal(dr);
                      return (
                        <Fragment key={idx}>
                          <tr>
                            <td className="pt-3 pb-1 pr-2">
                              <ProductPicker
                                value={dr.product_name}
                                products={products}
                                isDark={isDark}
                                isOpen={pickerOpenIdx === idx}
                                search={pickerOpenIdx === idx ? pickerSearch : ''}
                                brand={pickerOpenIdx === idx ? pickerBrand : 'all'}
                                brands={brands}
                                onToggle={() => {
                                  setPickerOpenIdx(pickerOpenIdx === idx ? null : idx);
                                  setPickerSearch(''); setPickerBrand('all');
                                }}
                                onSearch={setPickerSearch}
                                onBrand={setPickerBrand}
                                onSelect={(label, price) => {
                                  setDraftRow(idx, 'product_name', label);
                                  if (price != null) setDraftRow(idx, 'sold_at', String(price));
                                  setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all');
                                }}
                                onClose={() => { setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all'); }}
                              />
                            </td>
                            <td className="pt-3 pb-1 px-1">{numInput(dr.quantity, (v) => setDraftRow(idx, 'quantity', v), /[^0-9]/g, '0', 'int')}</td>
                            <td className="pt-3 pb-1 px-1">{numInput(dr.sold, (v) => setDraftRow(idx, 'sold', v), /[^0-9]/g, '0', 'int')}</td>
                            <td className="pt-3 pb-1 px-1">{numInput(dr.inhouse_use, (v) => setDraftRow(idx, 'inhouse_use', v), /[^0-9]/g, '0', 'int')}</td>
                            <td className="pt-3 pb-1 px-1">{numInput(dr.sold_at, (v) => setDraftRow(idx, 'sold_at', v), /[^0-9.]/g)}</td>
                            <td className="pt-3 pb-1 px-1">{numInput(dr.adjustment_amount, (v) => setDraftRow(idx, 'adjustment_amount', v), /[^0-9.\-]/g)}</td>
                            <td className="pt-3 pb-1 px-1">
                              <input
                                readOnly
                                value={tot.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                className={`${inp} text-right opacity-60 cursor-not-allowed`}
                              />
                            </td>
                            <td className="pt-3 pb-1 pl-2 text-center">
                              <button
                                onClick={() => removeDraftRow(idx)}
                                disabled={addModal.draftRows.length === 1}
                                className={`${muted} hover:text-red-500 disabled:opacity-30`}
                              >
                                <X size={15} />
                              </button>
                            </td>
                          </tr>
                          <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                            <td className="pb-3" />
                            <td colSpan={6} className="pb-3 pt-1 px-1">
                              <textarea
                                value={dr.remarks}
                                onChange={(e) => setDraftRow(idx, 'remarks', e.target.value)}
                                className={`${inp} resize-none text-xs`}
                                rows={1}
                                placeholder="Remarks (optional)"
                              />
                            </td>
                            <td className="pb-3" />
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={() => {
                    addDraftRow();
                    requestAnimationFrame(() => {
                      if (tableAreaRef.current)
                        tableAreaRef.current.scrollTo({ top: tableAreaRef.current.scrollHeight, behavior: 'smooth' });
                    });
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                    isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Plus size={13} /> Add Row
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => setAddModal(null)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveAdd}
                  disabled={addSaving || validCount === 0}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm"
                >
                  {addSaving ? 'Saving…' : `Save ${validCount} entr${validCount === 1 ? 'y' : 'ies'}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Edit single-row modal ───────────────────────────────────── */}
      {editModal && (() => {
        const period = periods.find((p) => p.id === editModal.periodId);
        const tot = rowTotal(editModal.v);
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setEditModal(null)}
          >
            <div
              className={`w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${card}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">Edit Sales Entry</h3>
                  {period && <p className={`text-sm ${muted}`}>{periodLabel(period)}</p>}
                </div>
                <button onClick={() => setEditModal(null)} className={muted}><X size={20} /></button>
              </div>

              {editError && <div className="mb-3 text-sm text-red-500">{editError}</div>}

              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Product <span className="text-red-500">*</span></label>
                  <ProductPicker
                    value={editModal.v.product_name}
                    products={products}
                    isDark={isDark}
                    isOpen={editPickerOpen}
                    search={editPickerSearch}
                    brand={editPickerBrand}
                    brands={brands}
                    onToggle={() => { setEditPickerOpen((o) => !o); setEditPickerSearch(''); setEditPickerBrand('all'); }}
                    onSearch={setEditPickerSearch}
                    onBrand={setEditPickerBrand}
                    onSelect={(label, price) => { setEV('product_name', label); if (price != null) setEV('sold_at', String(price)); setEditPickerOpen(false); }}
                    onClose={() => setEditPickerOpen(false)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Qty</label>
                    <input type="text" inputMode="numeric" value={editModal.v.quantity} placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEV('quantity', e.target.value.replace(/[^0-9]/g, ''))}
                      className={inp} />
                  </div>
                  <div>
                    <label className={labelCls}>Sold</label>
                    <input type="text" inputMode="numeric" value={editModal.v.sold} placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEV('sold', e.target.value.replace(/[^0-9]/g, ''))}
                      className={inp} />
                  </div>
                  <div>
                    <label className={labelCls}>Inhouse use</label>
                    <input type="text" inputMode="numeric" value={editModal.v.inhouse_use} placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEV('inhouse_use', e.target.value.replace(/[^0-9]/g, ''))}
                      className={inp} />
                  </div>
                  <div>
                    <label className={labelCls}>Sold at (₹)</label>
                    <input type="text" inputMode="decimal" value={editModal.v.sold_at} placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEV('sold_at', e.target.value.replace(/[^0-9.]/g, ''))}
                      className={inp} />
                  </div>
                  <div>
                    <label className={labelCls}>Adjustment (₹)</label>
                    <input type="text" inputMode="decimal" value={editModal.v.adjustment_amount} placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setEV('adjustment_amount', e.target.value.replace(/[^0-9.\-]/g, ''))}
                      className={inp} />
                  </div>
                  <div>
                    <label className={labelCls}>Total (auto)</label>
                    <input readOnly value={tot.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      className={`${inp} opacity-70 cursor-not-allowed`} />
                    <p className={`text-[10px] mt-1 ${muted}`}>sold × sold_at − adjustment</p>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Remarks (optional)</label>
                  <textarea value={editModal.v.remarks}
                    onChange={(e) => setEV('remarks', e.target.value)}
                    className={inp} rows={3} />
                </div>
              </div>

              <div className="flex gap-2 mt-5">
                <button onClick={saveEdit} disabled={editSaving}
                  className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg text-sm">
                  {editSaving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditModal(null)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
